import type { FetchError } from 'ofetch';
import type { _GettersTree } from 'pinia';

import * as Sentry from '@sentry/nuxt';
import { defineStore } from 'pinia';

import type { Cart, CartItem, CartRequest } from '~/stores/cart/types';

import { ApiMethod, handleApiError } from '~/utils/apiClient';
import { FetchState } from '~/utils/common';
import { StorageKey } from '~/utils/storage';

import { useAddressStore } from '../address/addressStore';
import { useAuthStore } from '../auth/authStore';

const STORE_ID = 'cart';

interface Actions {
  deleteCart: (locale: Locale) => Promise<void>;
  fetchCart: (locale: Locale) => Promise<Cart | null>;
  updateCart: (data: CartRequest) => Promise<Cart | null>;
}

interface Getters extends _GettersTree<State> {
  getCartItemsTotal: (state: State) => number;
  getCartLineItems: (state: State) => CartItem[];
}

interface State {
  cart: Cart | null;
  cartFetchState: FetchState;
}

export const useCartStore = defineStore<typeof STORE_ID, State, Getters, Actions>(STORE_ID, {
  actions: {
    async deleteCart(locale: Locale) {
      const currentCartId = getItemStorage(StorageKey.CURRENT_CART);
      try {
        await this.$api.fetch(ApiMethod.DELETE, '/carts', { id: currentCartId ?? undefined, locale }, true);
      } catch (error) {
        const fetchError = error as FetchError;
        await handleApiError(fetchError);

        return;
      }

      this.cart = null;
      this.cartFetchState = FetchState.FINISHED;
      Sentry.setContext('cart', null);
      saveItemStorage(StorageKey.CURRENT_CART, null);

      return;
    },
    async fetchCart(locale: Locale) {
      this.cartFetchState = FetchState.IN_PROGRESS;

      const addressStore = useAddressStore();

      const currentCartId = getItemStorage(StorageKey.CURRENT_CART);
      try {
        const response = await this.$api.fetch<Cart>(
          ApiMethod.GET,
          '/carts',
          { id: currentCartId ?? undefined, locale },
          true,
        );

        if (response?.data) {
          this.cart = response.data;
          this.cartFetchState = FetchState.SUCCEEDED;
          Sentry.setContext('cart', { id: response.data.id, itemCount: response.data.items.length });

          if (this.cart.shipping_address) {
            addressStore.currentShippingAddress = this.cart.shipping_address;
            addressStore.currentBillingAddress = { country: this.cart.shipping_address.country };
          }

          if (this.cart.billing_address) {
            addressStore.currentBillingAddress = this.cart.billing_address;
          }

          return response.data;
        }

        this.cartFetchState = FetchState.FINISHED;
        return null;
      } catch (error) {
        const fetchError = error as FetchError;
        await handleApiError(fetchError);

        this.cartFetchState = FetchState.ERRORED;
        return null;
      }
    },
    async updateCart(data: CartRequest) {
      const authStore = useAuthStore();
      const addressStore = useAddressStore();

      const currentCartId = getItemStorage(StorageKey.CURRENT_CART);
      try {
        const response = await this.$api.fetch<Cart>(
          ApiMethod.PUT,
          '/carts',
          { id: currentCartId ?? undefined, ...data },
          true,
        );

        if (response?.data) {
          this.cart = response.data;
          Sentry.setContext('cart', { id: response.data.id, itemCount: response.data.items.length });

          if (!authStore.isLoggedIn) saveItemStorage(StorageKey.CURRENT_CART, response.data.id);

          this.cartFetchState = FetchState.SUCCEEDED;

          if (this.cart.shipping_address) {
            addressStore.currentShippingAddress = this.cart.shipping_address;
          }

          if (this.cart.billing_address) {
            addressStore.currentBillingAddress = this.cart.billing_address;
          }

          return response.data;
        }
      } catch (error) {
        const fetchError = error as FetchError;
        await handleApiError(fetchError);
      }

      this.cartFetchState = FetchState.FINISHED;

      return null;
    },
  },
  getters: {
    getCartItemsTotal: (state: State) =>
      state.cart?.items.map((item) => item.quantity ?? 0).reduce((a, b) => a + b, 0) ?? 0,
    getCartLineItems: (state: State) =>
      state.cart?.items.sort((a, b) => (new Date(a.last_modified_at) > new Date(b.last_modified_at) ? -1 : 1)) ?? [],
  },
  state: () => ({ cart: null, cartFetchState: FetchState.INITIAL }),
});
